import { useEffect, useMemo } from 'react'
import { gql as contentfulGQL } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useQuery } from '@apollo/client/react'
import { EntryFields } from 'contentful'
import { slugs } from '@/constants/slugs'
import { ContentfulClientOptions, getContentfulClient } from '@/services/ApolloClient'
import { Faq, GetFaqsForProjectQuery } from '@/types/codegen-contentful'
import { logger } from '@/utils/logging'
import { contentfulFaqLocations } from './contentfulFaqLocations'

type FaqProject = {
  faq: {
    project: {
      slug: EntryFields.Text
    }
  }
}

type FaqCollectionResponse = {
  faqCollection: {
    items: Faq[]
  }
}

export const getFaqsForProjectByLocationQuery = contentfulGQL/* GraphQL */ `
  query getFaqsForProject($project: String!, $location: String!, $locale: String, $preview: Boolean!) {
    faqCollection(
      where: { OR: [{ project: { slug: $project } }, { project: { slug: "angel-studios" } }], location: $location }
      locale: $locale
      order: ordinal_ASC
      preview: $preview
      limit: 30
    ) {
      items {
        answer {
          json
        }
        ordinal
        hashLink
        project {
          slug
        }
        question
      }
    }
  }
`

const getProjectFromFaqQuery = contentfulGQL/* GraphQL */ `
  query getFaq($id: String!, $preview: Boolean!) {
    faq(id: $id, preview: $preview) {
      project {
        slug
      }
    }
  }
`

const extractFaqEntries = (response: FaqCollectionResponse | undefined) => {
  return response?.faqCollection?.items ?? []
}

const extractProjectSlug = (response: FaqProject | undefined) => {
  return response?.faq?.project?.slug
}

interface GetFaqsForProjectByLocationArgs {
  location: string
  opts: ContentfulClientOptions
  project: string
}

export const getFaqsForProjectByLocation = async ({ location, opts, project }: GetFaqsForProjectByLocationArgs) => {
  const contentfulClient = getContentfulClient({
    preview: opts.preview,
    locale: opts.locale,
  })

  const { data } = await contentfulClient.query<GetFaqsForProjectQuery>({
    query: getFaqsForProjectByLocationQuery,
    variables: {
      location,
      preview: opts.preview,
      project,
    },
    errorPolicy: 'all',
  })

  return extractFaqEntries(data as FaqCollectionResponse | undefined)
}

export const getProjectSlugFromFaq = async (id: string, opts: ContentfulClientOptions) => {
  const contentfulClient = getContentfulClient({
    locale: opts.locale,
    preview: opts.preview,
  })

  const { data } = await contentfulClient.query({
    query: getProjectFromFaqQuery,
    variables: {
      id,
      preview: opts.preview,
    },
    errorPolicy: 'all',
  })

  return extractProjectSlug(data as FaqProject | undefined)
}

interface UseProjectFaqsArgs {
  locale: string
  preview: boolean
  projectSlug?: string
  location?: string
}

export const useProjectFaqs = ({
  locale,
  preview,
  projectSlug,
  location,
}: UseProjectFaqsArgs): {
  faqs: Faq[]
  loadingProjectFaqs: boolean
  queryError: Error | undefined
} => {
  const client = getContentfulClient({ locale, preview })

  const {
    data: faqData,
    loading: loadingProjectFaqs,
    error: queryError,
  } = useQuery<GetFaqsForProjectQuery>(getFaqsForProjectByLocationQuery, {
    client,
    variables: {
      location: location ?? contentfulFaqLocations.home,
      project: projectSlug ?? slugs.angelStudios,
      locale: locale ?? 'en',
      preview,
    },
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (queryError) {
      logger().error(
        'Error fetching FAQs:',
        {
          queryError,
          projectSlug,
          location,
          locale,
          errorName: queryError.name,
          errorMessage: queryError.message,
          ...(CombinedGraphQLErrors.is(queryError)
            ? {
                graphQLErrors: queryError.errors.map((err) => ({
                  message: err.message,
                  path: err.path,
                  extensions: err.extensions,
                })),
              }
            : {}),
        },
        queryError,
      )
    }
  }, [queryError, projectSlug, location, locale])

  useEffect(() => {
    if (
      !loadingProjectFaqs &&
      !queryError &&
      (!faqData?.faqCollection?.items || faqData.faqCollection.items.length === 0)
    ) {
      logger().warn('No FAQs returned for query:', {
        projectSlug,
        location,
        locale,
        responseReceived: !!faqData,
        hasCollection: !!faqData?.faqCollection,
      })
    }
  }, [faqData, loadingProjectFaqs, queryError, projectSlug, location, locale])

  return useMemo(() => {
    return {
      faqs: faqData?.faqCollection?.items as Faq[],
      loadingProjectFaqs,
      queryError,
    }
  }, [faqData?.faqCollection?.items, loadingProjectFaqs, queryError])
}
